import React, { createContext, useState, useEffect, ReactNode, useCallback, useContext  } from 'react';
import { User } from '../types/user';
import apiClient from '../services/api'; // تأكد من اسم الملف
import * as authService from '../services/authService';
import { LoginData } from '../services/authService';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (data: LoginData) => Promise<void>;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);


export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true); // ابدأ بـ true دائماً للتحقق الأولي

  // !! تعديل: isAuthenticated الآن تعتمد مباشرة على وجود كائن المستخدم
  const isAuthenticated = !!user;

  // !! تعديل رئيسي: دالة للتحقق من صحة التوكن عند تحميل التطبيق
  const verifyAuth = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    // 1. تعيين التوكن في رأس الطلبات فوراً
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    try {
      // 2. إرسال طلب إلى الـ backend للحصول على بيانات المستخدم الحالية
      //    (يجب أن يكون لديك مسار مثل /api/auth/me أو /api/users/me)
      const response = await apiClient.get<{ success: boolean; data: User }>('/auth/me'); // <-- مسار مقترح
      
      if (response.data.success) {
        setUser(response.data.data); // تحديث المستخدم ببيانات حقيقية من الخادم
      } else {
        // إذا فشل التحقق (مثلاً، توكن منتهي الصلاحية)، قم بتسجيل الخروج
        handleLogout();
      }
    } catch (error) {
      console.error("Token verification failed:", error);
      handleLogout(); // تسجيل الخروج عند حدوث أي خطأ
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    verifyAuth();
  }, [verifyAuth]);

  // !! تعديل: دالة تسجيل الدخول مع معالجة الأخطاء
  const handleLogin = async (loginData: LoginData) => {
    try {
      // authService.login الآن تعيد { success, message, user, token }
      const responseData = await authService.login(loginData);
      
      // !! الإصلاح هنا !!
      // نحن نحتاج فقط إلى user و token من الاستجابة
      const { user, token } = responseData;

      localStorage.setItem('token', token);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);

    } catch (error) {
      handleLogout();
      throw error;
    }
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('token');
    delete apiClient.defaults.headers.common['Authorization'];
  };

  const contextValue = {
    user,
    isLoading,
    isAuthenticated,
    login: handleLogin,
    logout: handleLogout,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {/* لا تعرض أي شيء حتى ينتهي التحميل الأولي */}
      {!isLoading && children}
    </AuthContext.Provider>
  );
};
